import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgGridAngular } from 'ag-grid-angular';
import type {
  ColDef,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
} from 'ag-grid-community';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HlmAlertComponent, HlmBtnDirective, HlmIconComponent, HlmInputDirective } from '../../../ui';

/** Signature of the data fetcher passed by the parent dialog. */
export type CallDetailsTabFetchFn<T> = () => Observable<T[]>;

/**
 * Generic tab body used by the Call Details dialog:
 *   [ search input ] [ Download CSV ] -- [ AG-Grid (paginated, client-side filter) ]
 *
 * Each tab owns its own data; it fetches once on init and caches the result in local signals.
 * Loading, empty, and error states are rendered inline.
 */
@Component({
  selector: 'cmt-call-details-tab',
  standalone: true,
  imports: [
    AgGridAngular,
    HlmAlertComponent,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
  ],
  templateUrl: './call-details-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex min-h-0 flex-1 flex-col' },
})
export class CallDetailsTabComponent<T = unknown> implements OnInit {
  /** Tab display label, used in toolbar placeholder + filename. */
  readonly tabLabel = input.required<string>();
  /** Column defs passed by the dialog parent. */
  readonly columnDefs = input.required<ColDef<T>[]>();
  /** Filesystem-safe slug used in the CSV filename. */
  readonly fileNameBase = input.required<string>();
  /** Data fetcher. */
  readonly fetchFn = input.required<CallDetailsTabFetchFn<T>>();
  /**
   * If set, the tab shows this message instead of fetching (e.g. when callNoteId is null
   * and Call Notes / Billing cannot be loaded).
   */
  readonly unavailableReason = input<string | null>(null);

  /**
   * Optional AG-Grid full-width row hooks. Used by the CDR tab to render date
   * separator rows between groups of same-day events. When null, AG-Grid behaves
   * normally (all rows are regular row-renderer rows).
   */
  readonly isFullWidthRow = input<((params: { rowNode: { data: unknown } }) => boolean) | null>(null);
  readonly fullWidthCellRenderer = input<((params: { data: unknown }) => string) | null>(null);

  /**
   * Refresh trigger — incremented by the parent when an async lookup (e.g. CDR
   * agent name resolution) updates state that a cellRenderer reads. The effect
   * below forces AG-Grid to re-run its cellRenderers when this value changes.
   */
  readonly cellRefreshKey = input<number>(0);

  /**
   * Optional fixed row height. Used by tabs that need to accommodate multi-line
   * cell content (e.g. Call Notes' 2-line From/To renderer). null = AG-Grid
   * default (~42px in ag-theme-material).
   */
  readonly rowHeight = input<number | null>(null);

  constructor() {
    effect(() => {
      // Read the input so this effect tracks it as a dependency.
      this.cellRefreshKey();
      // gridApi is undefined before onGridReady; first run on key=0 is a no-op.
      this.gridApi?.refreshCells({ force: true });
    });
  }

  private readonly destroyRef = inject(DestroyRef);

  readonly rows = signal<T[]>([]);
  readonly loading = signal(false);
  readonly error = signal<{ status: number; message: string; correlationId?: string } | null>(null);
  readonly quickFilter = signal('');

  readonly defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: false,
    // Vertical centering — ag-theme-material relies on line-height for single
    // line cell content, which fails for flex/multi-line cellRenderers like
    // the 2-line From/To. `display:flex; align-items:center` guarantees every
    // cell's content sits in the row's vertical center regardless of how many
    // lines it spans.
    cellStyle: { display: 'flex', alignItems: 'center' },
  };

  readonly loadingOverlayTemplate =
    '<div class="flex items-center gap-3 text-sm text-muted-foreground"><svg class="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>Loading…</div>';

  readonly noRowsOverlayTemplate =
    '<div class="rounded-xl border border-dashed border-border bg-card/50 p-4 text-center text-sm text-muted-foreground">No data available</div>';

  private gridApi?: GridApi<T>;

  ngOnInit(): void {
    if (!this.unavailableReason()) {
      this.reload();
    }
  }

  onGridReady(event: GridReadyEvent<T>): void {
    this.gridApi = event.api;
    // Force cell refresh after every rowData update — AG-Grid v32+ occasionally
    // skips function-based string renderers on the initial render pass.
    event.api.addEventListener('rowDataUpdated', () => {
      event.api.refreshCells({ force: true });
    });
  }

  onFirstDataRendered(event: FirstDataRenderedEvent<T>): void {
    event.api.sizeColumnsToFit();
    // Force refresh so function-based string cellRenderers render on the initial pass.
    event.api.refreshCells({ force: true });
  }

  onGridSizeChanged(event: GridSizeChangedEvent): void {
    event.api.sizeColumnsToFit();
  }

  onQuickFilterInput(event: Event): void {
    this.quickFilter.set((event.target as HTMLInputElement).value);
  }

  onExportCsv(): void {
    const stamp = new Date();
    const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
    const date = `${stamp.getFullYear()}${pad(stamp.getMonth() + 1)}${pad(stamp.getDate())}`;
    this.gridApi?.exportDataAsCsv({
      fileName: `${this.fileNameBase()}-${date}.csv`,
      processCellCallback: (p) => (p.value == null ? '' : String(p.value)),
    });
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.gridApi?.showLoadingOverlay();
    this.fetchFn()()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.rows.set(data ?? []);
          this.loading.set(false);
          if ((data ?? []).length === 0) this.gridApi?.showNoRowsOverlay();
          else this.gridApi?.hideOverlay();
        },
        error: (err: HttpErrorResponse) => {
          this.rows.set([]);
          this.error.set({
            status: err.status ?? 0,
            message: err.message ?? 'Failed to load data',
            correlationId: err.headers?.get('correlation-id') ?? undefined,
          });
          this.loading.set(false);
          this.gridApi?.hideOverlay();
        },
      });
  }
}
